'use client';
import { getFileDropTarget, handledFileDropEvents, registerFileDropTarget, } from '#pplx/frontend/common/fileDropTargets.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// Document listeners still need accepted drops to clear every zone's drag state.
const elementHandledDropEvents = handledFileDropEvents;
function isFileDrag(dataTransfer) {
    return dataTransfer?.types.includes('Files') === true;
}
function getSupportedDragPayload(dataTransfer) {
    if (isFileDrag(dataTransfer))
        return 'file';
    // Drop consumers read text/plain; custom MIME markers alone must not activate an empty target.
    if (dataTransfer?.types.includes('text/plain') === true)
        return 'text';
    return null;
}
export function useDropZone({ onDrop, disabled = false, dropBehavior = 'file-element-only', validateDragItems, scope, }) {
    const [dragState, setDragState] = useState(null);
    const instanceId = useRef(Symbol());
    // Per instance: every mounted zone counts the same document events.
    const dragCount = useRef(0);
    const acceptsViewportDrops = !disabled && (dropBehavior === 'file-viewport-wide' || dropBehavior === 'all-viewport-wide');
    const isDragging = dragState !== null;
    const isDraggingFile = dragState?.payload === 'file';
    const isOver = dragState?.isOver === true;
    const isFileOver = isOver && isDraggingFile;
    const hasInvalidFiles = dragState?.hasInvalidFiles === true;
    // Same top-of-stack rule handleDocumentDrop uses to route viewport drops.
    const isViewportDropTarget = acceptsViewportDrops &&
        getFileDropTarget(dragState?.target ?? null, dragState?.payload !== 'text') ===
            instanceId.current;
    const handleDocumentDragOver = useCallback((e) => {
        // Suppress native browser handling; dropBehavior selects the receiver.
        e.preventDefault();
        setDragState((state) => state === null || state.target === e.target ? state : { ...state, target: e.target });
    }, []);
    const handleDocumentDragEnter = useCallback((e) => {
        const dataTransfer = e.dataTransfer;
        const payload = getSupportedDragPayload(dataTransfer);
        if (dataTransfer === null || payload === null) {
            return;
        }
        if (payload === 'file') {
            // Keep the browser from navigating to or opening a local file.
            e.preventDefault();
        }
        setDragState((previousState) => previousState?.payload === payload && previousState.target === e.target
            ? previousState
            : { payload, isOver: false, hasInvalidFiles: false, target: e.target });
        dragCount.current++;
    }, []);
    const handleDocumentDragLeave = useCallback((e) => {
        // Prevent default drag-and-drop behavior — custom DnD handling
        e.preventDefault();
        dragCount.current = Math.max(0, dragCount.current - 1);
        if (dragCount.current === 0) {
            setDragState(null);
        }
    }, []);
    const handleDocumentDrop = useCallback((e) => {
        const dataTransfer = e.dataTransfer;
        const handledByElement = elementHandledDropEvents.has(e);
        // Suppress native browser handling; dropBehavior selects the receiver.
        e.preventDefault();
        setDragState(null);
        dragCount.current = 0;
        const payload = getSupportedDragPayload(dataTransfer);
        const isTopOfStack = getFileDropTarget(e.target, payload === 'file') === instanceId.current;
        if (dataTransfer !== null &&
            payload !== null &&
            !handledByElement &&
            acceptsViewportDrops &&
            isTopOfStack &&
            (dropBehavior === 'all-viewport-wide' || payload === 'file') &&
            dataTransfer.items.length > 0) {
            elementHandledDropEvents.add(e);
            onDrop(dataTransfer.items);
        }
    }, [acceptsViewportDrops, dropBehavior, onDrop]);
    const handleElementDragOver = useCallback((e) => {
        const payload = getSupportedDragPayload(e.dataTransfer);
        if (payload === null) {
            setDragState(null);
            return;
        }
        // Prevent default drag-and-drop behavior — custom DnD handling
        e.preventDefault();
        const hasInvalidFiles = payload === 'file' && validateDragItems && e.dataTransfer.items.length > 0
            ? !validateDragItems(e.dataTransfer.items)
            : false;
        setDragState({
            payload,
            isOver: true,
            hasInvalidFiles,
            target: e.target,
        });
    }, [validateDragItems]);
    const handleElementDragLeave = useCallback((e) => {
        // Prevent default drag-and-drop behavior — custom DnD handling
        e.preventDefault();
        setDragState((previousState) => previousState === null ? null : { ...previousState, isOver: false, hasInvalidFiles: false });
    }, []);
    const handleElementDrop = useCallback((e) => {
        // Prevent default drag-and-drop behavior — custom DnD handling
        e.preventDefault();
        setDragState(null);
        const dataTransfer = e.dataTransfer;
        const payload = getSupportedDragPayload(dataTransfer);
        const acceptsElementDrop = !disabled &&
            !elementHandledDropEvents.has(e.nativeEvent) &&
            payload !== null &&
            dataTransfer.items.length > 0 &&
            (dropBehavior === 'all-element-only' ||
                (dropBehavior === 'file-element-only' && payload === 'file') ||
                dropBehavior === 'file-viewport-wide');
        if (acceptsElementDrop) {
            // The most specific accepting target owns the drop. Do not let a
            // viewport-wide document listener process the same payload again.
            elementHandledDropEvents.add(e.nativeEvent);
            onDrop(e.dataTransfer.items);
        }
    }, [disabled, dropBehavior, onDrop]);
    useEffect(() => {
        if (!acceptsViewportDrops) {
            return;
        }
        const id = instanceId.current;
        return registerFileDropTarget({ id, scope });
    }, [acceptsViewportDrops, scope]);
    useEffect(() => {
        document.addEventListener('dragover', handleDocumentDragOver);
        document.addEventListener('dragenter', handleDocumentDragEnter);
        document.addEventListener('dragleave', handleDocumentDragLeave);
        document.addEventListener('drop', handleDocumentDrop);
        return () => {
            document.removeEventListener('dragover', handleDocumentDragOver);
            document.removeEventListener('dragenter', handleDocumentDragEnter);
            document.removeEventListener('dragleave', handleDocumentDragLeave);
            document.removeEventListener('drop', handleDocumentDrop);
        };
    }, [
        handleDocumentDragOver,
        handleDocumentDragEnter,
        handleDocumentDragLeave,
        handleDocumentDrop,
    ]);
    return useMemo(() => ({
        isDragging,
        isDraggingFile,
        isOver,
        isFileOver,
        hasInvalidFiles,
        isViewportDropTarget,
        rootProps: {
            onDragOver: handleElementDragOver,
            onDragLeave: handleElementDragLeave,
            onDrop: handleElementDrop,
        },
    }), [
        handleElementDragLeave,
        handleElementDragOver,
        handleElementDrop,
        isDragging,
        isDraggingFile,
        isOver,
        isFileOver,
        hasInvalidFiles,
        isViewportDropTarget,
    ]);
}
//# sourceMappingURL=useDropZone.js.map